<template>
  <div class="w-full space-y-4 sm:space-y-6 text-slate-800 dark:text-[#F1F5F9]">
    <!-- ================= 1. HEADER CARD ================= -->
    <div
      class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-slate-200/90 dark:border-[#263449] shadow-xs transition-all"
    >
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <!-- Title & Icon Container -->
        <div class="flex items-start sm:items-center gap-3.5">
          <div
            class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20 flex-shrink-0"
          >
            <CreditCard class="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h2 class="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                Smart Cards Directory
              </h2>
              <span
                class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/40"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                {{ stats.total }} Enrolled Cards
              </span>
            </div>
            <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-0.5">
              Issue, monitor, and manage NFC/RFID digital identity credentials across campus
            </p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-2 sm:gap-2.5 flex-wrap sm:flex-nowrap">
          <button
            @click="refreshData"
            :disabled="isGridLoading || isInitialLoading"
            type="button"
            title="Refresh Card Directory"
            class="p-2.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#1e2d48] text-slate-600 dark:text-[#A6B4C8] transition active:scale-95 disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw
              :class="['w-4 h-4', isGridLoading ? 'animate-spin text-blue-600 dark:text-blue-400' : '']"
            />
          </button>

          <button
            @click="openBookingModal"
            :disabled="isBooking"
            type="button"
            class="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none text-white font-semibold text-xs sm:text-sm shadow-sm shadow-indigo-600/20 transition-all duration-150 cursor-pointer"
          >
            <CreditCard class="w-4 h-4" />
            <span>Book Smart Cards</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ================= 2. COMPACT 4-CARD STATS GRID ================= -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5">
      <!-- Card 1: Total Directory -->
      <div
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between transition-all"
      >
        <div class="flex items-center justify-between mb-2">
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center"
          >
            <CreditCard class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
          </div>
          <span class="text-[10px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider bg-slate-100 dark:bg-[#18243A] px-2 py-0.5 rounded-md">
            Directory
          </span>
        </div>
        <div>
          <div class="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9] leading-tight">
            {{ stats.total }}
          </div>
          <p class="text-[11px] sm:text-xs text-slate-500 dark:text-[#7E8DA5] font-medium mt-0.5 truncate">
            Total Enrolled IDs
          </p>
        </div>
        <div class="mt-2.5 pt-2 border-t border-slate-100 dark:border-[#263449]/70 flex items-center justify-between text-[11px] text-slate-500 dark:text-[#A6B4C8]">
          <span>System Total</span>
          <span class="font-semibold text-blue-600 dark:text-blue-400">100%</span>
        </div>
      </div>

      <!-- Card 2: Active Cards -->
      <div
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between transition-all"
      >
        <div class="flex items-center justify-between mb-2">
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center"
          >
            <CheckCircle2 class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
          </div>
          <span class="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-md">
            {{ activeRate }}%
          </span>
        </div>
        <div>
          <div class="text-xl sm:text-2xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400 leading-tight">
            {{ stats.active }}
          </div>
          <p class="text-[11px] sm:text-xs text-slate-500 dark:text-[#7E8DA5] font-medium mt-0.5 truncate">
            Active & Operational
          </p>
        </div>
        <!-- Micro Progress Bar -->
        <div class="mt-2.5 pt-2 border-t border-slate-100 dark:border-[#263449]/70">
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-1.5 rounded-full overflow-hidden">
            <div
              class="bg-emerald-500 h-full rounded-full transition-all duration-500"
              :style="{ width: `${activeRate}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Card 3: In Transit & Re-orders -->
      <div
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between transition-all"
      >
        <div class="flex items-center justify-between mb-2">
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center"
          >
            <Truck class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
          </div>
          <span class="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-md">
            Pipeline
          </span>
        </div>
        <div>
          <div class="text-xl sm:text-2xl font-bold tracking-tight text-amber-600 dark:text-amber-400 leading-tight">
            {{ stats.inTransit + stats.orderedAgain }}
          </div>
          <p class="text-[11px] sm:text-xs text-slate-500 dark:text-[#7E8DA5] font-medium mt-0.5 truncate">
            In Transit & Re-orders
          </p>
        </div>
        <div class="mt-2.5 pt-2 border-t border-slate-100 dark:border-[#263449]/70 flex items-center justify-between text-[11px] text-slate-500 dark:text-[#A6B4C8]">
          <span>{{ stats.inTransit }} Transit</span>
          <span class="text-purple-600 dark:text-purple-400 font-semibold">{{ stats.orderedAgain }} Re-ordered</span>
        </div>
      </div>

      <!-- Card 4: Campus Allocations -->
      <div
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between transition-all"
      >
        <div class="flex items-center justify-between mb-2">
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-violet-50 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 flex items-center justify-center"
          >
            <Users class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
          </div>
          <span class="text-[10px] font-bold text-violet-700 dark:text-violet-300 uppercase tracking-wider bg-violet-50 dark:bg-violet-950/60 px-2 py-0.5 rounded-md">
            Distribution
          </span>
        </div>
        <div>
          <div class="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9] leading-tight">
            {{ stats.students }} <span class="text-slate-400 text-sm font-normal">/</span> {{ stats.teachers }}
          </div>
          <p class="text-[11px] sm:text-xs text-slate-500 dark:text-[#7E8DA5] font-medium mt-0.5 truncate">
            Students vs Teachers
          </p>
        </div>
        <div class="mt-2.5 pt-2 border-t border-slate-100 dark:border-[#263449]/70 flex items-center justify-between text-[11px] text-slate-500 dark:text-[#A6B4C8]">
          <span class="text-blue-600 dark:text-blue-400 font-medium">Students ({{ stats.students }})</span>
          <span class="text-violet-600 dark:text-violet-400 font-medium">Staff ({{ stats.teachers }})</span>
        </div>
      </div>
    </div>

    <!-- ================= 3. COMMAND & FILTER BAR ================= -->
    <div
      class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs space-y-3"
    >
      <!-- Subheading / Filter Info -->
      <div class="flex items-center justify-between flex-wrap gap-2 text-xs">
        <div class="flex items-center gap-2 text-slate-700 dark:text-[#F1F5F9] font-bold">
          <SlidersHorizontal class="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span>Search & Filter Directory</span>
        </div>
        <span
          v-if="searchQuery || hasActiveFilters"
          class="text-[11px] text-blue-600 dark:text-blue-400 font-semibold"
        >
          Found {{ displayCards.length }} matching card{{ displayCards.length === 1 ? '' : 's' }}
        </span>
      </div>

      <!-- Controls Row -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 items-center">
        <!-- 🔍 SEARCH FIELD (Fixed with clear button & real-time responsiveness) -->
        <div class="relative" :class="selectedRole === 'student' ? 'lg:col-span-4' : 'lg:col-span-5'">
          <Search
            class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search member name, ID (#1002), email, or phone..."
            class="w-full py-2.5 pl-10 pr-9 text-xs sm:text-sm bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] transition focus:outline-none focus:bg-white dark:focus:bg-[#18243A] focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20"
          />
          <!-- Clear Button -->
          <button
            v-if="searchQuery"
            @click="clearSearch"
            type="button"
            title="Clear search"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded-full hover:bg-slate-200/50 dark:hover:bg-[#263449] transition cursor-pointer"
          >
            <X class="w-3.5 h-3.5" />
          </button>
        </div>

        <!-- 🎭 ROLE SEGMENTED SWITCHER -->
        <div
          class="p-1 bg-slate-100 dark:bg-[#18243A] rounded-xl border border-slate-200/80 dark:border-[#263449] grid grid-cols-3 gap-1"
          :class="selectedRole === 'student' ? 'lg:col-span-3' : 'lg:col-span-4'"
        >
          <button
            v-for="r in roleOptions"
            :key="r.value"
            @click="selectedRole = r.value"
            type="button"
            :class="[
              'py-1.5 px-2 rounded-lg text-xs font-semibold transition-all text-center truncate cursor-pointer',
              selectedRole === r.value
                ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/50 dark:border-[#263449]'
                : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-white',
            ]"
          >
            {{ r.label }}
          </button>
        </div>

        <!-- 🏫 CLASS FILTER (Conditional when Student role selected) -->
        <div
          v-if="selectedRole === 'student'"
          class="lg:col-span-2"
        >
          <ClassDropdown @selected-class="onClassSelected" />
        </div>

        <!-- 📦 STATUS FILTER -->
        <div
          :class="selectedRole === 'student' ? 'lg:col-span-2' : 'lg:col-span-2'"
        >
          <select
            v-model="selectedStatus"
            class="w-full py-2.5 px-3 text-xs sm:text-sm bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-700 dark:text-[#F1F5F9] transition focus:outline-none focus:bg-white dark:focus:bg-[#18243A] focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inTransit">In Transit</option>
            <option value="orderedAgain">Ordered Again</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>

        <!-- 🔄 RESET BUTTON -->
        <div
          v-if="hasActiveFilters"
          class="lg:col-span-1"
        >
          <button
            @click="clearFilters"
            title="Reset All Filters"
            type="button"
            class="w-full py-2.5 px-2 bg-slate-100 dark:bg-[#18243A] hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-600 dark:text-[#A6B4C8] hover:text-rose-600 dark:hover:text-rose-400 rounded-xl text-xs font-semibold tracking-wide transition-colors flex items-center justify-center gap-1 border border-slate-200/60 dark:border-[#263449] cursor-pointer"
          >
            <XCircle class="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        </div>
      </div>

      <!-- Active Filters Pill Tags (if any) -->
      <div
        v-if="hasActiveFilters"
        class="flex items-center gap-1.5 flex-wrap pt-1 text-xs"
      >
        <span class="text-[11px] text-slate-400 dark:text-[#7E8DA5] font-medium mr-1">Active:</span>

        <span
          v-if="searchQuery"
          class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/40 text-[11px] font-semibold"
        >
          Query: "{{ searchQuery }}"
          <button @click="clearSearch" class="hover:text-blue-900 cursor-pointer"><X class="w-3 h-3" /></button>
        </span>

        <span
          v-if="selectedRole"
          class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/40 text-[11px] font-semibold capitalize"
        >
          Role: {{ selectedRole }}
          <button @click="selectedRole = ''" class="hover:text-indigo-900 cursor-pointer"><X class="w-3 h-3" /></button>
        </span>

        <span
          v-if="selectedStatus"
          class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/40 text-[11px] font-semibold"
        >
          Status: {{ formatStatus(selectedStatus) }}
          <button @click="selectedStatus = ''" class="hover:text-emerald-900 cursor-pointer"><X class="w-3 h-3" /></button>
        </span>

        <span
          v-if="selectedClass"
          class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200/60 dark:border-purple-800/40 text-[11px] font-semibold"
        >
          Class: {{ selectedClass.name }}
          <button @click="onClassSelected(null)" class="hover:text-purple-900 cursor-pointer"><X class="w-3 h-3" /></button>
        </span>
      </div>
    </div>

    <!-- ================= 4. CARDS GRID & RESULTS AREA ================= -->
    <div class="relative min-h-[300px]">
      <!-- Initial Full Loader (Only on First Mount) -->
      <div
        v-if="isInitialLoading"
        class="py-20 bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs flex items-center justify-center"
      >
        <AppLoader
          variant="card"
          size="md"
          message="Loading Smart Card Records..."
          subtext="Fetching RFID tag associations and directory allocations"
          color="indigo"
        />
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center py-16 px-4 bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl border border-red-200 dark:border-red-900/40 shadow-xs text-center"
      >
        <div
          class="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-950/40 text-red-500 flex items-center justify-center mb-3"
        >
          <AlertCircle class="w-6 h-6" />
        </div>
        <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
          Failed to Load Smart Cards
        </h3>
        <p class="text-slate-500 dark:text-[#A6B4C8] text-xs max-w-sm mb-4">
          {{ error }}
        </p>
        <button
          class="px-4 py-2 bg-slate-900 dark:bg-slate-700 text-white rounded-xl font-semibold text-xs hover:bg-slate-800 transition cursor-pointer"
          @click="fetchData(1)"
        >
          Try Again
        </button>
      </div>

      <!-- Content State (With Non-Destructive Subtle Loader Overlay) -->
      <div v-else class="relative">
        <!-- Subtle Top Progress Indicator when fetching in background -->
        <div
          v-if="isGridLoading"
          class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 animate-pulse rounded-t-2xl z-10"
        ></div>

        <!-- Empty State -->
        <div
          v-if="displayCards.length === 0"
          class="text-center py-16 px-4 bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs"
        >
          <div
            class="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-[#18243A] text-slate-400 dark:text-[#7E8DA5] flex items-center justify-center mx-auto mb-3"
          >
            <LayoutGrid class="w-7 h-7" :stroke-width="1.5" />
          </div>
          <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
            No Smart Cards Found
          </h3>
          <p class="text-xs text-slate-400 dark:text-[#7E8DA5] max-w-sm mx-auto mb-4">
            {{ searchQuery ? `No records matched "${searchQuery}". Check the query or reset your filters.` : 'No cards have been issued under the selected filter criteria.' }}
          </p>
          <button
            v-if="hasActiveFilters"
            @click="clearFilters"
            type="button"
            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl transition shadow-xs cursor-pointer"
          >
            Clear Active Filters
          </button>
        </div>

        <!-- Cards Grid -->
        <div
          v-else
          :class="[
            'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4 transition-opacity duration-200',
            isGridLoading ? 'opacity-60 pointer-events-none' : 'opacity-100',
          ]"
        >
          <div
            v-for="card in displayCards"
            :key="card.smartCardId"
            class="group relative bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl border border-slate-200/90 dark:border-[#263449] hover:border-blue-400 dark:hover:border-blue-500/50 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col overflow-hidden"
          >
            <!-- Card Sub-header: Status Badge & SmartCard ID -->
            <div
              class="px-3.5 sm:px-4 py-2 border-b border-slate-100 dark:border-[#263449]/70 bg-slate-50/60 dark:bg-[#18243A]/40 flex items-center justify-between"
            >
              <span
                :class="[
                  'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold tracking-wide uppercase',
                  card.status === 'active'
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-600/20'
                    : card.status === 'disabled'
                    ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 ring-1 ring-rose-600/20'
                    : card.status === 'orderedAgain'
                    ? 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 ring-1 ring-purple-600/20'
                    : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 ring-1 ring-amber-600/20',
                ]"
              >
                <span
                  :class="[
                    'w-1.5 h-1.5 rounded-full',
                    card.status === 'active'
                      ? 'bg-emerald-500 animate-pulse'
                      : card.status === 'disabled'
                      ? 'bg-rose-500'
                      : card.status === 'orderedAgain'
                      ? 'bg-purple-500 animate-pulse'
                      : 'bg-amber-500',
                  ]"
                ></span>
                {{ formatStatus(card.status) }}
              </span>

              <div class="flex items-center gap-1.5">
                <CreditCard class="w-3 h-3 text-slate-400 dark:text-[#7E8DA5]" />
                <span class="text-[11px] font-mono font-bold text-slate-600 dark:text-[#A6B4C8]">
                  #{{ card.smartCardId }}
                </span>
              </div>
            </div>

            <!-- Profile Info Body -->
            <div class="p-3.5 sm:p-4 flex-1 flex gap-3.5 items-start">
              <!-- Avatar Column -->
              <div class="relative flex-shrink-0">
                <div
                  class="w-12 h-12 min-w-[48px] max-w-[48px] min-h-[48px] max-h-[48px] sm:w-14 sm:h-14 sm:min-w-[56px] sm:max-w-[56px] sm:min-h-[56px] sm:max-h-[56px] rounded-2xl overflow-hidden ring-2 ring-slate-100 dark:ring-[#263449] bg-slate-50 dark:bg-[#18243A] flex items-center justify-center shadow-xs shrink-0"
                >
                  <img
                    v-if="card.photo && !imageErrors[card.smartCardId]"
                    :src="card.photo"
                    :alt="card.name"
                    class="w-full h-full object-cover"
                    @error="markImageError(card.smartCardId)"
                  />
                  <div
                    v-else
                    :style="{ backgroundColor: getAvatarColor(card.name) }"
                    class="w-full h-full flex items-center justify-center text-white font-bold text-base sm:text-lg uppercase"
                  >
                    {{ getInitial(card.name) }}
                  </div>
                </div>

                <!-- Role Pill -->
                <span
                  :class="[
                    'absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider shadow-xs text-white',
                    card.role === 'student' ? 'bg-blue-600' : 'bg-violet-600',
                  ]"
                >
                  {{ card.role }}
                </span>
              </div>

              <!-- Content Column -->
              <div class="min-w-0 flex-1">
                <h3
                  class="text-sm sm:text-base font-bold text-slate-900 dark:text-[#F1F5F9] capitalize truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                >
                  {{ card.name }}
                </h3>
                <p class="text-xs text-slate-400 dark:text-[#7E8DA5] truncate mb-2">
                  {{ card.schoolName }}
                </p>

                <div class="space-y-1 text-xs text-slate-600 dark:text-[#A6B4C8]">
                  <div class="flex items-center gap-2 truncate">
                    <Mail class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] flex-shrink-0" />
                    <span class="truncate">{{ card.email || "No email" }}</span>
                  </div>

                  <div class="flex items-center gap-2 truncate">
                    <Phone class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] flex-shrink-0" />
                    <span class="truncate">{{ card.phone || "No phone" }}</span>
                  </div>

                  <div
                    v-if="card.dob"
                    class="flex items-center gap-2 truncate text-[11px]"
                  >
                    <Calendar class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] flex-shrink-0" />
                    <span>{{ formatDate(card.dob) }}</span>
                  </div>
                </div>

                <!-- Student Tags (Class & Roll) -->
                <div
                  v-if="card.role === 'student'"
                  class="flex gap-1.5 mt-2.5 flex-wrap"
                >
                  <span
                    v-if="card.className"
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-[#18243A] rounded-md text-[11px] font-medium text-slate-600 dark:text-[#A6B4C8] border border-slate-200/50 dark:border-[#263449]"
                  >
                    <BookOpen class="w-3 h-3 text-slate-400" />
                    {{ card.className }}
                  </span>
                  <span
                    v-if="card.rollNumber"
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-[#18243A] rounded-md text-[11px] font-medium text-slate-600 dark:text-[#A6B4C8] border border-slate-200/50 dark:border-[#263449]"
                  >
                    <Hash class="w-3 h-3 text-slate-400" />
                    {{ card.rollNumber }}
                  </span>
                </div>

                <!-- Reorder Tag if re-ordered -->
                <div
                  v-if="card.isReordered || card.status === 'orderedAgain'"
                  class="mt-2"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200/60 dark:border-purple-800/40"
                  >
                    <RotateCw class="w-3 h-3" />
                    Reordered {{ card.reorderCount ? `(${card.reorderCount}x)` : '' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Card Bottom Bar: Actions -->
            <div
              class="px-3.5 sm:px-4 py-2.5 bg-slate-50/70 dark:bg-[#18243A]/40 border-t border-slate-100 dark:border-[#263449]/70 flex items-center gap-2"
            >
              <button
                @click="viewCard(card)"
                type="button"
                class="flex-1 flex items-center justify-center gap-1.5 py-1.5 sm:py-2 px-3 rounded-xl text-xs font-semibold bg-white dark:bg-[#172235] hover:bg-slate-100 dark:hover:bg-[#1f2c42] text-slate-700 dark:text-[#F1F5F9] border border-slate-200 dark:border-[#263449] transition shadow-xs active:scale-[0.98] cursor-pointer"
              >
                <Eye class="w-3.5 h-3.5 text-slate-500 dark:text-[#7E8DA5]" />
                <span>Profile</span>
              </button>

              <button
                @click="openSingleReorderModal(card)"
                type="button"
                class="flex-1 flex items-center justify-center gap-1.5 py-1.5 sm:py-2 px-3 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white shadow-xs transition cursor-pointer"
                title="Order Smart Card again for this member"
              >
                <RotateCw class="w-3.5 h-3.5" />
                <span>Order Again</span>
              </button>
            </div>
          </div>
        </div>

        <!-- ================= 5. PAGINATION CONTROLS ================= -->
        <div
          v-if="pagination && pagination.pages > 1"
          class="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 mt-6 border-t border-slate-200/90 dark:border-[#263449]"
        >
          <span class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] font-medium order-2 sm:order-1">
            Showing page {{ pagination.page }} of {{ pagination.pages }} ({{ pagination.total }} total records)
          </span>

          <div class="inline-flex items-center gap-1.5 order-1 sm:order-2">
            <!-- First -->
            <button
              class="w-8 h-8 rounded-lg bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition text-xs font-bold cursor-pointer"
              :disabled="pagination.page === 1"
              @click="changePage(1)"
            >
              «
            </button>

            <!-- Prev -->
            <button
              class="w-8 h-8 rounded-lg bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition flex items-center justify-center cursor-pointer"
              :disabled="pagination.page === 1"
              @click="changePage(pagination.page - 1)"
            >
              <ChevronLeft class="w-4 h-4" />
            </button>

            <!-- Number Buttons -->
            <div class="hidden sm:flex gap-1">
              <template
                v-for="page in getVisiblePages()"
                :key="page"
              >
                <span
                  v-if="page === '...'"
                  class="w-8 h-8 flex items-center justify-center text-slate-400 dark:text-[#7E8DA5] text-xs"
                >
                  ...
                </span>
                <button
                  v-else
                  :class="[
                    'w-8 h-8 rounded-lg text-xs font-semibold transition cursor-pointer',
                    page === pagination.page
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:border-indigo-500 hover:text-indigo-600',
                  ]"
                  @click="changePage(page)"
                >
                  {{ page }}
                </button>
              </template>
            </div>

            <!-- Next -->
            <button
              class="w-8 h-8 rounded-lg bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition flex items-center justify-center cursor-pointer"
              :disabled="pagination.page === pagination.pages"
              @click="changePage(pagination.page + 1)"
            >
              <ChevronRight class="w-4 h-4" />
            </button>

            <!-- Last -->
            <button
              class="w-8 h-8 rounded-lg bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition text-xs font-bold cursor-pointer"
              :disabled="pagination.page === pagination.pages"
              @click="changePage(pagination.pages)"
            >
              »
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= 6. BOOKING CATEGORY MODAL ================= -->
    <div
      v-if="showOptionsModal"
      class="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
      @click="closeOptionsModal"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl p-5 sm:p-6 max-w-md w-full border border-slate-200 dark:border-[#263449] animate-in zoom-in-95 duration-150"
        @click.stop
      >
        <div class="mb-5 flex items-start justify-between">
          <div>
            <h2 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-[#F1F5F9]">
              Select Booking Category
            </h2>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-1">
              Choose which category of members to order smart cards for
            </p>
          </div>
          <button
            @click="closeOptionsModal"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg cursor-pointer"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="space-y-3">
          <button
            @click="selectBookingOption('teachers')"
            type="button"
            class="w-full text-left p-3.5 border border-slate-200 dark:border-[#263449] hover:border-violet-500 dark:hover:border-violet-500 hover:bg-violet-50/50 dark:hover:bg-violet-950/20 rounded-xl transition group flex items-start gap-3 cursor-pointer"
          >
            <div
              class="p-2.5 rounded-xl bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400 group-hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
            >
              <UserCog class="w-5 h-5" />
            </div>
            <div>
              <div class="font-semibold text-slate-900 dark:text-[#F1F5F9] text-sm">
                Book for Teachers & Staff
              </div>
              <div class="text-xs text-slate-500 dark:text-[#7E8DA5] mt-0.5">
                Generate and allocate cards for all staff members
              </div>
            </div>
          </button>

          <button
            @click="selectBookingOption('students')"
            type="button"
            class="w-full text-left p-3.5 border border-slate-200 dark:border-[#263449] hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 rounded-xl transition group flex items-start gap-3 cursor-pointer"
          >
            <div
              class="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
            >
              <GraduationCap class="w-5 h-5" />
            </div>
            <div>
              <div class="font-semibold text-slate-900 dark:text-[#F1F5F9] text-sm">
                Book for Enrolled Students
              </div>
              <div class="text-xs text-slate-500 dark:text-[#7E8DA5] mt-0.5">
                Generate batch cards for all active students
              </div>
            </div>
          </button>

          <button
            @click="selectBookingOption('both')"
            type="button"
            class="w-full text-left p-3.5 border border-slate-200 dark:border-[#263449] hover:border-emerald-500 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 rounded-xl transition group flex items-start gap-3 cursor-pointer"
          >
            <div
              class="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 group-hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
            >
              <CreditCard class="w-5 h-5" />
            </div>
            <div>
              <div class="font-semibold text-slate-900 dark:text-[#F1F5F9] text-sm">
                Book for Entire Campus (Everyone)
              </div>
              <div class="text-xs text-slate-500 dark:text-[#7E8DA5] mt-0.5">
                Full allocation for both teachers and students
              </div>
            </div>
          </button>
        </div>

        <button
          @click="closeOptionsModal"
          type="button"
          class="w-full mt-4 px-4 py-2.5 text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-xl transition font-semibold text-xs sm:text-sm cursor-pointer"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- ================= 7. BOOKING CONFIRMATION MODAL ================= -->
    <BookingConfirmationModal
      :is-open="showBookingModal"
      :booking-type="selectedOption"
      :is-loading="isBooking"
      @confirm="handleConfirmedBooking"
      @cancel="closeBookingModal"
    />

    <!-- ================= 8. SINGLE CARD REORDER MODAL ================= -->
    <div
      v-if="showSingleReorderModal && reorderingTargetCard"
      class="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
      @click="closeSingleReorderModal"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl p-5 sm:p-6 max-w-sm w-full border border-slate-200 dark:border-[#263449] animate-in zoom-in-95 duration-150"
        @click.stop
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center flex-shrink-0"
          >
            <RotateCw class="w-5 h-5" />
          </div>
          <div>
            <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-[#F1F5F9]">
              Order Smart Card Again
            </h2>
            <p class="text-xs text-slate-500 dark:text-[#7E8DA5]">
              Card re-order confirmation
            </p>
          </div>
        </div>

        <div class="mb-5 space-y-2.5">
          <p class="text-xs sm:text-sm text-slate-700 dark:text-[#A6B4C8] leading-relaxed">
            Are you sure you want to order a new Smart Card again for
            <strong class="text-slate-900 dark:text-white capitalize">{{ reorderingTargetCard.name }}</strong>
            (#{{ reorderingTargetCard.smartCardId }})?
          </p>
          <div
            class="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800/40 rounded-xl p-3 text-[11px] text-purple-800 dark:text-purple-300"
          >
            <span>🔄 This will mark the card as <strong>"Ordered Again"</strong> in Smart Card Operations.</span>
          </div>
        </div>

        <div class="flex gap-2">
          <button
            @click="closeSingleReorderModal"
            :disabled="isReorderingSingle"
            type="button"
            class="flex-1 py-2.5 px-3 border border-slate-200 dark:border-[#263449] text-slate-700 dark:text-[#A6B4C8] hover:bg-slate-50 dark:hover:bg-[#18243A] rounded-xl font-semibold text-xs transition disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            @click="confirmSingleReorder"
            :disabled="isReorderingSingle"
            type="button"
            class="flex-1 py-2.5 px-3 bg-purple-600 hover:bg-purple-700 active:scale-[0.98] text-white rounded-xl font-semibold text-xs transition disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-xs cursor-pointer"
          >
            <RotateCw :class="['w-3.5 h-3.5', isReorderingSingle ? 'animate-spin' : '']" />
            <span>{{ isReorderingSingle ? "Ordering..." : "Confirm Order" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import {
    CreditCard,
    Search,
    Users,
    CheckCircle2,
    Truck,
    GraduationCap,
    UserCog,
    LayoutGrid,
    Mail,
    Phone,
    Calendar,
    BookOpen,
    Hash,
    Eye,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    XCircle,
    RotateCw,
    X,
    SlidersHorizontal,
    RefreshCw,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import BookingConfirmationModal from "@/components/SmartCardAndDevice/ConfirmationModal.vue";
  import ClassDropdown from "@/components/SmartCardAndDevice/classFilter.vue";

  /* ================= STORE & ROUTER ================= */
  const router = useRouter();
  const store = useStore();
  const { showToast } = useGlobalToast();

  /* ================= BOOKING MODALS ================= */
  const showOptionsModal = ref(false);
  const showBookingModal = ref(false);
  const selectedOption = ref("");
  const isBooking = ref(false);

  /* ================= SINGLE CARD REORDER ================= */
  const showSingleReorderModal = ref(false);
  const reorderingTargetCard = ref(null);
  const isReorderingSingle = ref(false);

  const openSingleReorderModal = (card) => {
    reorderingTargetCard.value = card;
    showSingleReorderModal.value = true;
  };

  const closeSingleReorderModal = () => {
    showSingleReorderModal.value = false;
    reorderingTargetCard.value = null;
  };

  const confirmSingleReorder = async () => {
    if (!reorderingTargetCard.value) return;
    isReorderingSingle.value = true;

    try {
      await store.dispatch("bookSmartCard", {
        userId: reorderingTargetCard.value.userId,
        reorder: true,
      });

      showToast(
        "success",
        `Smart card ordered again for ${reorderingTargetCard.value.name}!`,
      );

      closeSingleReorderModal();
      await store.dispatch("fetchSmartCardStats");
      fetchData(pagination.value?.page || 1);
    } catch (err) {
      console.error("Reorder card error:", err);
      showToast("error", "Failed to order smart card again.");
    } finally {
      isReorderingSingle.value = false;
    }
  };

  const openBookingModal = () => {
    showOptionsModal.value = true;
  };

  const closeOptionsModal = () => {
    showOptionsModal.value = false;
  };

  const selectBookingOption = (option) => {
    selectedOption.value = option;
    closeOptionsModal();
    showBookingModal.value = true;
  };

  const closeBookingModal = () => {
    showBookingModal.value = false;
    selectedOption.value = "";
  };

  const handleConfirmedBooking = async () => {
    isBooking.value = true;

    try {
      let payload = {};

      if (selectedOption.value === "students") payload.allStudents = true;
      else if (selectedOption.value === "teachers") payload.allTeachers = true;
      else if (selectedOption.value === "both") payload.both = true;

      const res = await store.dispatch("bookSmartCard", payload);

      if (res?.reorderedCards?.length > 0 && res?.createdCards?.length > 0) {
        showToast(
          "success",
          `Smart cards booked: ${res.createdCards.length} created, ${res.reorderedCards.length} ordered again!`,
        );
      } else if (res?.reorderedCards?.length > 0) {
        showToast(
          "success",
          `Smart cards ordered again for ${res.reorderedCards.length} ${selectedOption.value}!`,
        );
      } else if (res?.createdCards?.length > 0) {
        showToast(
          "success",
          `Smart Cards booked successfully for ${res.createdCards.length} ${selectedOption.value}!`,
        );
      } else if (res?.skippedUsers?.length > 0) {
        showToast(
          "info",
          `Smart cards already exist for all selected ${selectedOption.value}.`,
        );
      } else {
        showToast(
          "success",
          `Smart Cards processed successfully!`,
        );
      }

      await store.dispatch("fetchSmartCardStats");
      closeBookingModal();
      fetchData(1);
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to book Smart Cards. Please try again.");
    } finally {
      isBooking.value = false;
    }
  };

  /* ================= FILTERS & SEARCH STATE ================= */
  const searchQuery = ref("");
  const selectedRole = ref("");
  const selectedStatus = ref("");
  const selectedClass = ref(null);
  const imageErrors = ref({});

  const roleOptions = [
    { label: "All Roles", value: "" },
    { label: "Students", value: "student" },
    { label: "Teachers", value: "teacher" },
  ];

  // Loading States: isInitialLoading for 1st mount, isGridLoading for search/filter operations
  const isInitialLoading = ref(true);
  const isGridLoading = ref(false);
  const error = ref(null);
  let searchDebounceTimer = null;

  /* ================= COMPUTED PROPERTIES ================= */
  const smartCards = computed(() => store.state.smartCards || []);
  const pagination = computed(() => store.state.cardsPagination);
  const serverStats = computed(() => store.state.smartCardStats || {});

  const hasActiveFilters = computed(() => {
    return Boolean(
      searchQuery.value ||
      selectedRole.value ||
      selectedStatus.value ||
      selectedClass.value
    );
  });

  // Calculate accurate school stats
  const stats = computed(() => {
    const s = serverStats.value;
    const all = smartCards.value;

    const total = s.totalCards ?? all.length;
    const active = s.totalActive ?? all.filter((c) => c.status === "active").length;
    const inTransit = s.totalInTransit ?? all.filter((c) => c.status === "inTransit").length;
    const orderedAgain = s.totalOrderedAgain ?? all.filter((c) => c.status === "orderedAgain" || c.isReordered).length;
    const disabled = s.totalDisabled ?? all.filter((c) => c.status === "disabled").length;
    const students = s.totalStudentCards ?? all.filter((c) => c.role === "student").length;
    const teachers = s.totalTeacherCards ?? all.filter((c) => c.role === "teacher").length;

    return {
      total,
      active,
      inTransit,
      orderedAgain,
      disabled,
      students,
      teachers,
    };
  });

  const activeRate = computed(() => {
    if (!stats.value.total) return 0;
    return Math.round((stats.value.active / stats.value.total) * 100);
  });

  // Instant client-side filtering on the active cards set
  const displayCards = computed(() => {
    let list = smartCards.value;
    const query = searchQuery.value.trim().toLowerCase();

    if (query) {
      list = list.filter((card) => {
        const nameMatch = card.name?.toLowerCase().includes(query);
        const emailMatch = card.email?.toLowerCase().includes(query);
        const phoneMatch = card.phone?.toLowerCase().includes(query);
        const idMatch = String(card.smartCardId || "").toLowerCase().includes(query);
        const classMatch = card.className?.toLowerCase().includes(query);
        const rollMatch = String(card.rollNumber || "").toLowerCase().includes(query);
        const roleMatch = card.role?.toLowerCase().includes(query);
        return nameMatch || emailMatch || phoneMatch || idMatch || classMatch || rollMatch || roleMatch;
      });
    }

    if (selectedRole.value) {
      list = list.filter((card) => card.role === selectedRole.value);
    }

    if (selectedStatus.value) {
      list = list.filter((card) => card.status === selectedStatus.value);
    }

    if (selectedClass.value?._id) {
      list = list.filter((card) => String(card.classId) === String(selectedClass.value._id));
    }

    return list;
  });

  /* ================= DATA FETCHING ================= */
  const fetchData = async (page = 1) => {
    isGridLoading.value = true;
    error.value = null;

    try {
      const payload = {
        page,
        limit: 18,
        search: searchQuery.value.trim(),
        classId: selectedClass.value?._id || null,
        role: selectedRole.value || null,
        status: selectedStatus.value || null,
      };

      await store.dispatch("fetchAllSmartCards", payload);
    } catch (err) {
      error.value =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch smart cards";
      console.error("❌ Error fetching smart cards:", error.value);
    } finally {
      isGridLoading.value = false;
      isInitialLoading.value = false;
    }
  };

  const refreshData = async () => {
    store.dispatch("fetchSmartCardStats");
    await fetchData(pagination.value?.page || 1);
  };

  /* ================= SEARCH & FILTER HANDLERS ================= */
  const onSearchInput = () => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
      fetchData(1);
    }, 350);
  };

  const clearSearch = () => {
    searchQuery.value = "";
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    fetchData(1);
  };

  const onClassSelected = (cls) => {
    selectedClass.value = cls;
    fetchData(1);
  };

  const clearFilters = () => {
    searchQuery.value = "";
    selectedRole.value = "";
    selectedStatus.value = "";
    selectedClass.value = null;
    fetchData(1);
  };

  /* ================= PAGINATION ================= */
  const changePage = (page) => {
    if (page >= 1 && pagination.value && page <= pagination.value.pages) {
      fetchData(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getVisiblePages = () => {
    if (!pagination.value) return [];

    const pages = [];
    const current = pagination.value.page;
    const total = pagination.value.pages;
    const delta = 2;

    for (
      let i = Math.max(1, current - delta);
      i <= Math.min(total, current + delta);
      i++
    ) {
      pages.push(i);
    }

    if (pages[0] > 1) {
      if (pages[0] > 2) pages.unshift("...");
      pages.unshift(1);
    }

    if (pages[pages.length - 1] < total) {
      if (pages[pages.length - 1] < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  /* ================= HELPERS & FORMATTERS ================= */
  const viewCard = async (card) => {
    if (card.role === "teacher") {
      router.push({
        name: "Teacher",
        query: {
          userId: card.userId,
        },
      });
    } else {
      await store.dispatch("fetchUserById", card.userId);
      router.push({
        name: "Students",
        query: {
          userId: card.userId,
        },
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatStatus = (status) => {
    const statusMap = {
      active: "Active",
      disabled: "Disabled",
      inTransit: "In Transit",
      orderedAgain: "Ordered Again",
    };
    return statusMap[status] || status;
  };

  const markImageError = (id) => {
    imageErrors.value[id] = true;
  };

  const getInitial = (name) => {
    if (!name) return "?";
    return name.trim().charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#6366F1",
      "#0EA5E9",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
    ];

    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  /* ================= WATCHERS ================= */
  watch(searchQuery, onSearchInput);
  watch(selectedStatus, () => fetchData(1));
  watch(selectedRole, (newRole) => {
    if (newRole !== "student") {
      selectedClass.value = null;
    }
    fetchData(1);
  });

  /* ================= LIFECYCLE ================= */
  onMounted(async () => {
    store.dispatch("fetchSmartCardStats");
    await fetchData(1);
  });
</script>
